import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Play, Save, Trash2 } from 'lucide-react';
import { del, get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { humanize, money } from '@/lib/utils';
import { useTranslation } from '@/lib/i18n';
import { ConfirmDialog, DataTable, Dialog } from '@/components/ui/layout';
import { Alert, Badge, Button, Card, CardContent, CardHeader, CardTitle, Field, Input, Select } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useToast } from '@/components/ui/toast';

interface Dataset {
  code: string;
  label: string;
  dateField: string;
  fields: string[];
}

interface RunResult {
  dataset: string;
  mode: 'rows' | 'grouped';
  columns: string[];
  rows: Record<string, unknown>[];
}

interface SavedReport {
  id: string;
  code: string;
  name: string;
}

const AGGREGATES = ['count', 'sum', 'avg', 'min', 'max'] as const;

/**
 * Custom report builder (SRS 10.4).
 *
 * Fields come from the server's whitelist rather than from a free-text box, so
 * what the screen offers and what the API will accept are the same list.
 */
export function ReportBuilder() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [dataset, setDataset] = useState('');
  const [fields, setFields] = useState<string[]>([]);
  const [groupBy, setGroupBy] = useState<string[]>([]);
  const [aggregates, setAggregates] = useState<{ field: string; fn: string; alias: string }[]>([]);
  const [result, setResult] = useState<RunResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveForm, setSaveForm] = useState({ code: '', name: '' });
  const [deleting, setDeleting] = useState<{ id: string; name: string } | null>(null);
  const common = useTranslation('common');

  const datasets = useQuery({
    queryKey: ['builder-datasets'],
    queryFn: () => get<Dataset[]>('/reports/builder/datasets'),
  });

  const saved = useQuery({
    queryKey: ['saved-reports'],
    queryFn: () => get<SavedReport[]>('/reports/saved'),
  });

  const spec = datasets.data?.find((d) => d.code === dataset);

  const definition = () => ({
    dataset,
    fields: fields.length ? fields : groupBy,
    groupBy,
    aggregates: aggregates.filter((a) => a.field),
    limit: 200,
  });

  const run = useMutation({
    mutationFn: () => post<RunResult>('/reports/builder/run', definition()),
    onSuccess: (data) => {
      setError(null);
      setResult(data);
    },
    onError: (caught) => {
      setResult(null);
      setError(describeError(caught));
    },
  });

  const save = useMutation({
    mutationFn: () => post('/reports/saved', { ...saveForm, definition: definition() }),
    onSuccess: () => {
      toast.success('Report saved');
      void queryClient.invalidateQueries({ queryKey: ['saved-reports'] });
      setSaving(false);
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const runSaved = useMutation({
    mutationFn: (id: string) => get<RunResult>(`/reports/saved/${id}/run`),
    onSuccess: (data) => {
      setError(null);
      setResult(data);
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => del(`/reports/saved/${id}`),
    onSuccess: () => {
      toast.success('Report deleted');
      void queryClient.invalidateQueries({ queryKey: ['saved-reports'] });
    },
    onError: (caught) => toast.error('Could not delete it', describeError(caught)),
  });

  const toggle = (list: string[], value: string, set: (next: string[]) => void) =>
    set(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

  const format = (column: string, value: unknown) => {
    if (value === null || value === undefined) return '—';
    if (column.endsWith('Minor') || column === 'billed') return money(Number(value));
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) return value.slice(0, 10);
    return String(value);
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Build a query</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Field label="Dataset" required>
              <Select
                value={dataset}
                onChange={(e) => {
                  setDataset(e.target.value);
                  setFields([]);
                  setGroupBy([]);
                  setAggregates([]);
                  setResult(null);
                }}
              >
                <option value="">Select…</option>
                {datasets.data?.map((d) => (
                  <option key={d.code} value={d.code}>
                    {d.label}
                  </option>
                ))}
              </Select>
            </Field>

            {spec && (
              <>
                <div>
                  <p className="mb-1.5 text-sm font-medium">Columns</p>
                  <div className="flex flex-wrap gap-1">
                    {spec.fields.map((field) => (
                      <button
                        key={field}
                        type="button"
                        onClick={() => toggle(fields, field, setFields)}
                        className="focus-ring rounded-full"
                      >
                        <Badge variant={fields.includes(field) ? 'default' : 'muted'}>{field}</Badge>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="mb-1.5 text-sm font-medium">Group by</p>
                  <div className="flex flex-wrap gap-1">
                    {spec.fields.map((field) => (
                      <button
                        key={field}
                        type="button"
                        onClick={() => toggle(groupBy, field, setGroupBy)}
                        className="focus-ring rounded-full"
                      >
                        <Badge variant={groupBy.includes(field) ? 'success' : 'muted'}>{field}</Badge>
                      </button>
                    ))}
                  </div>
                </div>

                {groupBy.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Aggregates</p>
                    {aggregates.map((aggregate, index) => (
                      <div key={index} className="space-y-1.5">
                        <Select
                          value={aggregate.field}
                          onChange={(e) =>
                            setAggregates(aggregates.map((a, i) => (i === index ? { ...a, field: e.target.value } : a)))
                          }
                        >
                          <option value="">Field…</option>
                          {spec.fields.map((field) => (
                            <option key={field} value={field}>
                              {field}
                            </option>
                          ))}
                        </Select>
                        <ChoiceButtons
                          size="sm"
                          inline
                          aria-label="Aggregate"
                          value={aggregate.fn}
                          onChange={(fn) => setAggregates(aggregates.map((a, i) => (i === index ? { ...a, fn } : a)))}
                          options={AGGREGATES.map((fn) => ({ value: fn, label: humanize(fn) }))}
                        />
                      </div>
                    ))}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setAggregates([...aggregates, { field: '', fn: 'count', alias: '' }])}
                    >
                      Add aggregate
                    </Button>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button onClick={() => run.mutate()} loading={run.isPending} disabled={!fields.length && !groupBy.length}>
                    <Play className="h-4 w-4" /> Run
                  </Button>
                  <Button variant="outline" onClick={() => setSaving(true)} disabled={!result}>
                    <Save className="h-4 w-4" /> Save
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <div className="space-y-4">
          {error && <Alert variant="destructive">{error}</Alert>}

          {result && (
            <Card>
              <CardHeader className="flex-row items-center justify-between">
                <CardTitle className="text-sm">
                  {humanize(result.dataset)} · {result.rows.length} row{result.rows.length === 1 ? '' : 's'}
                </CardTitle>
                <Badge variant="secondary">{humanize(result.mode)}</Badge>
              </CardHeader>
              <CardContent>
                <DataTable
                  dense
                  rows={result.rows}
                  rowKey={(row) => JSON.stringify(row).slice(0, 80)}
                  empty="No rows matched."
                  columns={result.columns.map((column) => ({
                    header: column,
                    align: column.endsWith('Minor') || column === 'count' ? ('right' as const) : ('left' as const),
                    cell: (row: Record<string, unknown>) => format(column, row[column]),
                  }))}
                />
              </CardContent>
            </Card>
          )}

          {(saved.data?.length ?? 0) > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Saved reports</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-1.5">
                  {saved.data?.map((report) => (
                    <div key={report.id} className="flex items-center justify-between rounded-md border px-3 py-2 text-sm">
                      <div>
                        <p className="font-medium">{report.name}</p>
                        <p className="font-mono text-xs text-muted-foreground">{report.code}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => runSaved.mutate(report.id)}>
                          Run
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label={common.t('action.delete')}
                          title={common.t('action.delete')}
                          onClick={() => setDeleting({ id: report.id, name: report.name })}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {deleting && (
        <ConfirmDialog
          subject={deleting.name}
          destructive
          confirmLabel={common.t('action.delete')}
          onConfirm={() => remove.mutateAsync(deleting.id)}
          onClose={() => setDeleting(null)}
        />
      )}

      <Dialog
        open={saving}
        onClose={() => setSaving(false)}
        title="Save this report"
        description="Saved reports can be re-run without rebuilding the query"
        footer={
          <>
            <Button variant="ghost" onClick={() => setSaving(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => save.mutate()}
              loading={save.isPending}
              disabled={!saveForm.code || !saveForm.name}
            >
              Save
            </Button>
          </>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Code" required>
            <Input
              value={saveForm.code}
              onChange={(e) => setSaveForm({ ...saveForm, code: e.target.value.toUpperCase() })}
              placeholder="REVENUE-BY-CONTEXT"
            />
          </Field>
          <Field label="Name" required>
            <Input value={saveForm.name} onChange={(e) => setSaveForm({ ...saveForm, name: e.target.value })} />
          </Field>
        </div>
      </Dialog>
    </div>
  );
}
